import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { PushMessage } from '@models/domain/push-message';
import { firstValueFrom, Observable, Subject } from 'rxjs';

export type PushPermission = NotificationPermission | 'unsupported';

const PROMPT_DISMISSED_KEY = 'tucuate.push_prompt_dismissed';
const PUSH_DISABLED_KEY = 'tucuate.push_disabled';

@Injectable({ providedIn: 'root' })
export class MessagingService {
  private token: string | null = null;
  private foregroundMessage = new Subject<PushMessage>();

  /**
   * Pushes recibidos con la web abierta y visible. En ese caso FCM no muestra la notificación del
   * sistema (solo se la entrega a la página), así que quien la consuma decide cómo mostrarla.
   */
  public foregroundMessages$: Observable<PushMessage> = this.foregroundMessage.asObservable();

  constructor(private angularFireMessaging: AngularFireMessaging) {}

  public get supported(): boolean {
    return typeof Notification !== 'undefined' && 'serviceWorker' in navigator;
  }

  public get permission(): PushPermission {
    return this.supported ? Notification.permission : 'unsupported';
  }

  /**
   * Push realmente activo en este navegador. El permiso nativo no sirve por sí solo: el usuario
   * puede desactivar las notificaciones desde la app (se borra el token de FCM) y el navegador
   * seguirá reportando `granted`, así que la desactivación se recuerda aparte.
   */
  public get enabled(): boolean {
    return this.supported && this.permission === 'granted' && localStorage.getItem(PUSH_DISABLED_KEY) !== 'true';
  }

  /**
   * Antes de disparar el diálogo nativo del navegador (que solo se puede pedir una vez: si el
   * usuario lo rechaza ahí, ya no hay forma de volver a preguntar sin que él mismo lo habilite
   * manualmente), mostramos nuestro propio aviso explicativo. Este getter decide si corresponde
   * mostrarlo: solo si el navegador soporta push, todavía no se decidió el permiso nativo
   * (`default`), y el usuario no descartó antes nuestro aviso.
   */
  public get shouldPromptForPermission(): boolean {
    return this.supported && this.permission === 'default' && localStorage.getItem(PROMPT_DISMISSED_KEY) !== 'true';
  }

  public dismissPermissionPrompt(): void {
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
  }

  public async requestToken(): Promise<string | null> {
    const token = await firstValueFrom(this.angularFireMessaging.requestToken);
    this.token = token;
    if (token) localStorage.removeItem(PUSH_DISABLED_KEY);
    console.log('[FCM] Token:', token);
    return token;
  }

  public async deleteToken(): Promise<void> {
    await firstValueFrom(this.angularFireMessaging.deleteToken(this.token ?? ''));
    this.token = null;
    // Solo si el token se borró de verdad: si falla, el dispositivo sigue recibiendo notificaciones.
    localStorage.setItem(PUSH_DISABLED_KEY, 'true');
  }

  public listen(): void {
    this.angularFireMessaging.messages.subscribe((payload) => {
      // Log para probar: muestra el payload completo tal como lo entrega FCM.
      console.log('[FCM] Foreground message:', payload);
      this.foregroundMessage.next({
        title: payload.notification?.title ?? null,
        body: payload.notification?.body ?? null,
        image: payload.notification?.image ?? null,
        icon: payload.data?.['icon'] ?? null,
        link: payload.data?.['link'] ?? null,
        linkLabel: payload.data?.['link_label'] ?? null,
        linkTarget: payload.data?.['link_target'] ?? null,
        data: payload.data ?? {},
      });
    });
  }
}
